/**
 * Decide como reproduzir um sinal e qual é o seu status offline.
 *
 * Prioridade de reprodução (PLAN §17):
 *   1. arquivo offline válido
 *   2. origem MP4 direta autorizada
 *   3. player online
 *   4. indisponível
 *
 * O ponto sensível é o passo 1: a disponibilidade offline é confirmada **no
 * filesystem**, nunca pela flag do banco (PLAN F10-T11). Um registro dizendo
 * `status: 'available'` cujo arquivo sumiu é exatamente o cenário que arruina uma
 * viagem, e é justamente o que a flag sozinha não detecta.
 */
import type { Sign } from '@/types/catalog';
import { getState } from '@/db/repositories/localSignState';
import {
  AuthorizedDirectMediaSource,
  OnlineYoutubeMediaSource,
} from '@/services/media/sources';
import type {
  LocalMediaLookup,
  MediaAvailability,
  MediaResolution,
  MediaSource,
  PlaybackPlan,
} from '@/services/media/types';

export interface MediaResolverOptions {
  /** Origem de arquivos locais. Ausente = nada está disponível offline ainda. */
  localMedia?: LocalMediaLookup | undefined;
  downloadSource?: MediaSource;
  onlineSource?: MediaSource;
}

export class MediaResolver {
  private readonly localMedia: LocalMediaLookup | undefined;
  private readonly downloadSource: MediaSource;
  private readonly onlineSource: MediaSource;

  constructor(options: MediaResolverOptions = {}) {
    this.localMedia = options.localMedia;
    this.downloadSource = options.downloadSource ?? new AuthorizedDirectMediaSource();
    this.onlineSource = options.onlineSource ?? new OnlineYoutubeMediaSource();
  }

  /** Se existe alguma origem capaz de produzir um arquivo baixável. */
  canDownload(sign: Sign): boolean {
    return this.downloadSource.canDownload(sign);
  }

  resolveDownload(sign: Sign): Promise<MediaResolution> {
    return this.downloadSource.resolve(sign);
  }

  /**
   * Status para a UI (F4-T11).
   *
   * Distingue quatro situações que o usuário precisa diferenciar antes de viajar:
   * já está no dispositivo, dá para baixar, só funciona online, ou não há mídia.
   */
  async availability(sign: Sign): Promise<MediaAvailability> {
    const state = await getState(sign.id);

    if (state.offline.status === 'error') return 'error';

    if (this.localMedia && (await this.localMedia.has(sign.id))) {
      return 'offline-available';
    }

    if (this.canDownload(sign)) return 'downloadable';

    if (sign.media.provider === 'youtube' && sign.media.externalId) return 'online-only';

    return 'unavailable';
  }

  /**
   * Monta o plano de reprodução.
   *
   * Quem chamar precisa liberar o Object URL quando `isObjectUrl` for `true`
   * (PLAN §35 — Blob URLs devem ser liberadas).
   */
  async playbackPlan(sign: Sign): Promise<PlaybackPlan> {
    // 1. arquivo local
    if (this.localMedia) {
      try {
        const blob = await this.localMedia.read(sign.id);
        if (blob && blob.size > 0) {
          return {
            kind: 'offline',
            url: URL.createObjectURL(blob),
            mimeType: blob.type || 'video/mp4',
            isObjectUrl: true,
          };
        }
      } catch (error) {
        // Arquivo corrompido ou ilegível não pode impedir a reprodução online.
        console.warn(`[media] falha ao ler arquivo local de ${sign.id}`, error);
      }
    }

    // 2. MP4 direto autorizado
    if (this.downloadSource.canDownload(sign)) {
      const resolution = await this.downloadSource.resolve(sign);
      if (resolution.url) {
        return {
          kind: 'direct',
          url: resolution.url,
          mimeType: resolution.mimeType ?? 'video/mp4',
          isObjectUrl: false,
        };
      }
    }

    // 3. player online
    const online = await this.onlineSource.resolve(sign);
    if (online.url) {
      return { kind: 'embed', url: online.url, isObjectUrl: false };
    }

    // 4. nada disponível — estado legítimo, não é erro
    return {
      kind: 'unavailable',
      isObjectUrl: false,
      reason: 'Este sinal não tem vídeo associado no catálogo.',
    };
  }
}

/** Texto curto de cada status, para a UI. */
export const AVAILABILITY_LABEL: Record<MediaAvailability, string> = {
  'offline-available': 'Disponível offline',
  downloadable: 'Pode ser baixado',
  'online-only': 'Somente online',
  unavailable: 'Sem vídeo',
  error: 'Erro no download',
};
